import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { StepProps } from "../../types";
import { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import ChoosePreventativeCarePackage from "components/Signup/components/ChoosePreventiveCarePackage/ChoosePreventiveCarePackage";
import { getPreventiveCareAddOnLimits } from "utils/site";
import { trackSignupEvent } from "components/Signup/analytics";
import { useSignupStore } from "components/Signup/store/hooks";
import {
  getCurrentDraftForAnimal,
  getDraft,
  useAllValidAnimals,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import {
  updateInsuranceById,
  updatePreventiveAddonLimitOnAll,
} from "components/Signup/store/slices/animals";
import { UpsertDraft_upsertDraft_policyPreviews_plans } from "../../../../../../__generated__/UpsertDraft";
import { amountToPay as amountToPayWithoutDiscount } from "utils/insurance";
import Overlay from "components/Signup/components/Overlay";
import { ReactNode, useState } from "react";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import { BuyButton } from "components/Signup/components/BuyButton";
import { formatCurrency, isDefined } from "utils/utils";
import Gutter from "ui-kit/Gutter";
import {
  useIsLoading,
  useMetadata,
} from "components/Signup/store/selectors/metadata";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { updateApplyPreventiveAddonToAll } from "components/Signup/store/slices/metadata";
import { Typography } from "ui-kit/Typography";
import { PopupContent } from "components/Signup/components/ChoosePreventiveCarePackage/PopupContent";
import { Trans, useWebTranslation } from "translations";
import Colors from "ui-kit/colors";
import Image from "next/image";

const getPreventivePackageCost = ({
  value,
  drafts,
  limit,
  copay,
}: {
  value: number;
  drafts: UpsertDraft_upsertDraft_policyPreviews_plans[] | null | undefined;
  limit: number;
  copay: number;
}) => {
  const noPreventivePackagePrice = (drafts || null)?.find((preview) => {
    return (
      preview?.limit === limit &&
      preview.coverages?.find(
        ({ coverageGroupId }) => coverageGroupId === "Preventive"
      )?.limit === undefined &&
      preview.copay === copay
    );
  })?.netPremium;

  const preventivePackagePrice = (drafts || null)?.find((preview) => {
    return (
      preview?.limit === limit &&
      preview.coverages?.find(
        ({ coverageGroupId }) => coverageGroupId === "Preventive"
      )?.limit === value &&
      preview.copay === copay
    );
  })?.netPremium;

  if (!noPreventivePackagePrice || !preventivePackagePrice) {
    return 0;
  }

  return preventivePackagePrice - noPreventivePackagePrice;
};

const Preventive = ({ next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();

  const animals = useAllValidAnimals();

  const animal = useCurrentAnimal();

  const {
    billingInterval,
    limit,
    copay,
    preventiveCareAddOnLimit,
    drafts,
    id,
    product,
  } = useCurrentInsuranceByAnimalId(animal.id);
  const { applyPreventiveCareToAll } = useMetadata();
  const { dispatch, getState } = useSignupStore();
  const isLoading = useIsLoading();

  const amountToPay = getAmountToPay({
    ...getDraft({
      drafts,
      product,
      limit,
      copay,
      preventiveCareAddOnLimit: preventiveCareAddOnLimit ?? 0,
    }),
    billingInterval,
  });

  const currentQuote = getCurrentDraftForAnimal(animal);

  const [overlayDisplay, setOverlayDisplay] = useState<{
    title: string;
    content: ReactNode;
    full?: boolean;
  } | null>(null);

  return (
    <>
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Typography variant="h2" align="center">
          {t("signup.preventive_step.title", {
            defaultValue:
              "Économisez sur les soins préventifs {{name, genitive_case}}",
            name: animal.name ?? "",
          })}
        </Typography>
        <Gutter size={2} />
        <Typography
          variant="body2"
          align="center"
          component="div"
          color="grey500"
        >
          <Trans
            i18nKey="signup.preventive_step.description"
            defaults="
          ⚡️ Couverture immédiate pour vaccins et antiparasitaires, frais
          d'identification, castration et stérilisation, et <0>plus encore</0>.
          "
            components={[
              <button
                key={0}
                style={{
                  all: "inherit",
                  color: Colors.blue300,
                  fontWeight: 600,
                  display: "inline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setOverlayDisplay({
                    title: t("insurance.preventive_care_package_description"),
                    content: <PopupContent />,
                  });
                }}
              />,
            ]}
          />
        </Typography>
        <Gutter size={2} />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Image
            src="/images/preventive-avatars.svg"
            alt=""
            width={58}
            height={24}
          />
          <Typography variant="body3">
            <Trans
              i18nKey="signup.preventive_step.avatar_byline"
              components={[<strong key="0" />]}
              defaults="Adopté par <0>+15 000 propriétaires.</0>"
            />
          </Typography>
        </div>
        <Gutter size={2} />
        <ChoosePreventativeCarePackage
          currentTotal={amountToPayWithoutDiscount({
            ...currentQuote,
            billingInterval,
          })}
          reflectOnAllInsurances={applyPreventiveCareToAll ?? false}
          onChangeReflectOnAllInsurances={() => {
            dispatch(
              updateApplyPreventiveAddonToAll({
                limit: preventiveCareAddOnLimit ?? 0,
                active: !applyPreventiveCareToAll,
              })
            );
          }}
          annualBilling={billingInterval === 12}
          currentPreventiveCareAddOnLimit={preventiveCareAddOnLimit}
          lowestPrice={getPreventivePackageCost({
            value: getPreventiveCareAddOnLimits()[0],
            copay,
            limit,
            drafts,
          })}
          limits={getPreventiveCareAddOnLimits().map((value) => ({
            value,
            total:
              (getPreventivePackageCost({
                value,
                drafts,
                limit,
                copay,
              }) /
                12) *
              billingInterval,
          }))}
          onChange={(newValue) => {
            if (!isDefined(newValue)) {
              return;
            }

            if (newValue !== 0) {
              const state = getState();
              trackSignupEvent("Preventive Care Package selected", 0, state);
            }

            if (applyPreventiveCareToAll) {
              dispatch(updatePreventiveAddonLimitOnAll(newValue));
            } else {
              dispatch(
                updateInsuranceById({
                  id,
                  update: {
                    preventiveCareAddOnLimit: newValue,
                  },
                })
              );
            }
          }}
          onInfo={({ title, content }) =>
            setOverlayDisplay({
              title,
              content,
            })
          }
        />
        <Overlay
          display={overlayDisplay}
          onRemove={() => setOverlayDisplay(null)}
        />
      </div>

      {animals.length > 1 ? (
        <>
          <QuotePreviewBottomBar
            next={next}
            title={applyPreventiveCareToAll ? t("global.next") : undefined}
            onPress={
              applyPreventiveCareToAll
                ? () => {
                    next(false, "health-questions");
                  }
                : undefined
            }
          />
        </>
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  amountToPay.old ? formatCurrency(amountToPay.old) : null
                }
                newAmount={formatCurrency(amountToPay.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={!isValid}
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}
    </>
  );
};

export default Preventive;
